import { ArtistState } from "explorer/reducers/artistas";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReactComponent as StarIcon } from "shared/assets/images/icon-star.svg";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";

export interface MenuTagProps {
  variant?: "pro" | "stage" | "soon";
  stageRestriction?: number;
}

const colorAndBGByStatus: {
  [key: string]: { color: string; background: string };
} = {
  pro: {
    color: "#F2F2F2",
    background: "linear-gradient(90deg, #F680C8 0%, #4846CC 100%), #F034A6",
  },
  stage: { color: "#9966FF", background: "rgba(153, 102, 255, 0.20)" },
  available: { color: "#66D7EA", background: "rgba(0, 189, 220, 0.20)" },
};

const MenuTag: React.FC<MenuTagProps> = ({ variant, stageRestriction }) => {
  const { t } = useTranslation();
  const { currentPlan } = useSubscription();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const artistsStage = userArtist?.stage || 0;

  const status = useMemo(() => {
    if (variant === "pro") return "pro";
    if (variant === "stage" && stageRestriction) {
      return artistsStage > stageRestriction ? "available" : "stage";
    }
    return "stage";
  }, [variant, stageRestriction, artistsStage]);

  const { color, background } = colorAndBGByStatus[status];

  const ProContent = () => (
    <span className="menu-tag-content body-small">
      <StarIcon /> Pro
    </span>
  );

  const LevelRestriction = () => (
    <span className="menu-tag-content body-small">
      <StarIcon stroke={colorAndBGByStatus["stage"].color} />{" "}
      {t("components.MenuLateral.estagio")} {stageRestriction}
    </span>
  );

  const ComingSoon = () => (
    <span className="menu-tag-content body-small">
      {t("components.MenuLateral.soon")}
    </span>
  );

  const Available = () => (
    <span className="menu-tag-content body-small">
      {t("components.MenuLateral.available")}
    </span>
  );

  const isPremiumOrSimilar = [
    SubscriptionPlan.PREMIUM,
    SubscriptionPlan.DISTRIBUTION,
    SubscriptionPlan.LABEL_SERVICES,
  ].includes(currentPlan || SubscriptionPlan.START);

  const isDistributionOnly = currentPlan === SubscriptionPlan.DISTRIBUTION;

  if (variant === "pro" && isPremiumOrSimilar) {
    return null;
  }

  if (variant === "stage" && isDistributionOnly) {
    return null;
  }

  if (!currentPlan && variant !== "soon") return null;

  return (
    <div className="menu-tag" style={{ color, background }}>
      {variant === "pro" && <ProContent />}

      {variant === "stage" &&
        stageRestriction &&
        (artistsStage > stageRestriction ? (
          <Available />
        ) : (
          <LevelRestriction />
        ))}

      {variant === "soon" && <ComingSoon />}
    </div>
  );
};

export default MenuTag;
